import React, { useState } from "react";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import ReactPaginate from "react-paginate";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { getUser} from "../../../utils/Common";
import { useSystem } from "../../../hooks/useSystem";
import { FaFileExcel, FaFilePdf } from "react-icons/fa";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const DataTable = ({ 
    columns, 
    data, tableTitle = "Resultados", 
    filename = "report-cotel",
    nameReport = "Reporte CORREOS PANAMÁ", 
    exportExcel, 
    exportPdf, 
    exportableColumns, 
    includeTotals, 
    sucursalSelected,
    IdSucursal,
    IdCaja,
    logTypeExcel,
    logTypePdf,
    IdProvincia,
    customHeader,
    firmaSecundaria 
}) => {
    const user = getUser();

    const { Logs } = useSystem();
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
        page,
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: 20 },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );
    const [customPageSize, setCustomPageSize] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);

    const handlePageSizeChange = (e) => {
        const value = e.target.value;
        if (value === "custom") {
            setShowCustomInput(true);
        } else {
            setShowCustomInput(false);
            setPageSize(Number(value));
            setCustomPageSize('');
        }
    };

    const handleCustomPageSizeChange = (e) => {
        const value = e.target.value;
        setCustomPageSize(value);
        const numericValue = Number(value);
        if (numericValue > 0) {
            setPageSize(numericValue);
        }
    };

    const formatExportData = (data, columns) => {
        return data.map(row => {
            const formattedRow = {};
            columns.forEach(col => {
                if (col.Cell) {
                    formattedRow[col.Header] = col.Cell({ value: row[col.accessor] });
                } else {
                    formattedRow[col.Header] = row[col.accessor];
                }
            });
            return formattedRow;
        });
    };

    const handleMessageElaborated = async (user) => {
        switch (user.roleId) {
            case 1:
                return `Agente de Estafeta: ${user.CodigoEstafeta} ${user.NombreEstafeta}`;
            case 2:
                return `Recaudador de Estafeta: ${user.CodigoEstafeta} ${user.NombreEstafeta}`;
            case 3:
                return `Jefe/Supervisor de Estafeta: ${user.CodigoEstafeta} ${user.NombreEstafeta}`;
            case 4:
                return `Recaudador provincial de Provincia: ${user.NombreProvincia}`;
            case 5:
                return `Contador provincial de Provincia: ${user.NombreProvincia}`;
            case 6:
                return `Administrador Provincial`;
            case 7:
                return `Consultante`;
            case 8:
                return `Soporte Tecnico `;
            case 9:
                return `Super Administrador`;
            default:
                return `Usuario de sistema`;
        }
    };

    const exportFileLogs = async (typeLog, IdSucursal, IdCaja, IdProvincia) => {
        const dataToSend = {
            IdTipoLog: typeLog,
            IdSucursal: IdSucursal,
            IdCaja: IdCaja,
            IdProvincia: IdProvincia,
        };

        const abortController = new AbortController();
        try {
            await Logs(abortController.signal, dataToSend); 
        } catch (error) {
            console.error(error);
        }
    };


    const exportToPDF = async () => {
        const formattedData = formatExportData(data, exportableColumns ?? columns);
        const tableColumnHeaders = Object.keys(formattedData[0]);
        const isLandscape = tableColumnHeaders.length > 10;

        const doc = new jsPDF({
            orientation: isLandscape ? "landscape" : "portrait",
            unit: "px",
            format: "a4",
        });

        let logo = new Image();
        logo.src = "/img/logo-correos.png";
        const logoWidth = 110;
        const logoHeight = 40;
        
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = pageWidth - 40 - logoWidth; 
        doc.addImage(logo, "PNG", logoX, 20, logoWidth, logoHeight);

        doc.setFont("Helvetica", "bold");
        doc.setFontSize(20); 
        const titleX = 40; 
        const maxTitleWidth = pageWidth - logoWidth - 80; // 40px de margen a cada lado
        const titleLines = doc.splitTextToSize(nameReport.toUpperCase(), maxTitleWidth);
        const titleY = 40; // Volver a la posición original para el título
        doc.text(titleLines, titleX, titleY);

        // --- Encabezado personalizado o por defecto ---
        let infoY = titleY + (titleLines.length * 15) + 20; // Empieza después del título, con espacio extra
        const lineSpacing = 12;
        if (customHeader) {
            let headerLines = [];
            if (typeof customHeader === 'string') {
                headerLines = customHeader.split(/\n|\r\n?/).filter(line => line.trim() !== '');
            } else if (Array.isArray(customHeader) && customHeader.every(item => typeof item === 'string')) {
                headerLines = customHeader;
            }
            if (headerLines.length > 0) {
                doc.setFont("Helvetica", "bold");
                doc.setFontSize(13);
                headerLines.forEach((line, idx) => {
                    doc.text(line, 40, infoY + idx * lineSpacing);
                });
                infoY += headerLines.length * lineSpacing;
            } else if (Array.isArray(customHeader) && customHeader.every(item => typeof item === 'object' && item.label && item.value)) {
                doc.setFont("Helvetica", "normal");
                doc.setFontSize(9);
                customHeader.forEach((item, idx) => {
                    doc.text(`${item.label} ${item.value}`, 40, infoY + idx * lineSpacing);
                });
                infoY += customHeader.length * lineSpacing;
            }
        } else {
            const sucursal = sucursalSelected ?? `${getUser().CodigoEstafeta} ${getUser().NombreEstafeta}`;
            const usuario = getUser().Usuario;
            const fecha = new Date();
            const fechaStr = fecha.toLocaleDateString("es-ES", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
            const horaStr = fecha.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: true });
            doc.setFont("Helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`Estafeta: ${sucursal}`, 40, infoY);
            infoY += lineSpacing;
            doc.text(`Fecha de Reporte: ${fechaStr}`, 40, infoY);
            infoY += lineSpacing;
            doc.text(`Hora: ${horaStr}`, 40, infoY);
            infoY += lineSpacing;
            doc.text(`Usuario: ${usuario}`, 40, infoY);
            infoY += lineSpacing;
        }

        // --- Espacio extra antes de la tabla ---
        infoY += 20;

        const tableRows = formattedData.map(row => Object.values(row));

        let totals = [];
        if (includeTotals) {
            totals = Array(tableColumnHeaders.length).fill("");
            const columnsToUse = exportableColumns || columns;
            columnsToUse.forEach((col, colIndex) => {
                if (col.isTotalizable && col.accessor) {
                    let total = 0;
                    data.forEach(row => {
                        let valor = row[col.accessor];
                        if (typeof valor === "string") {
                            valor = valor.replace(/[B\/.\s,]/g, '');
                            valor = parseFloat(valor);
                        }
                        if (!isNaN(valor) && valor !== null && valor !== undefined) {
                            total += Number(valor);
                        }
                    });
                    const headerIndex = tableColumnHeaders.findIndex(header => header === col.Header);
                    if (headerIndex !== -1) {
                        totals[headerIndex] = `B/. ${total.toFixed(2)}`;
                    }
                }
            });
            if (totals.every(total => total === "")) {
                totals[0] = "TOTAL";
            }
        }

        const noteRow = Array(tableColumnHeaders.length).fill("");
        noteRow[0] = "Nota: Los colaboradores son responsables de todas las actividades realizadas con su cuenta de usuario en el sistema y deben asegurarse de que este documento sea utilizado exclusivamente por Correos Panamá.";

        const contactRow = Array(tableColumnHeaders.length).fill("");
        contactRow[0] = "www.correospanama.gob.pa | Síguenos en: @correospma | Teléfonos: 512-7657 | WhatsApp: 6980-1551";

        let isLastPage = false;
        doc.autoTable({
            head: [tableColumnHeaders],
            body: [...tableRows, totals, noteRow, contactRow],
            startY: infoY,
            styles: { fontSize: 8, cellPadding: 1, halign: "center" },
            headStyles: { fillColor: [15, 67, 114], textColor: [255, 255, 255], fontStyle: "bold" },
            bodyStyles: { lineColor: [0, 0, 0], lineWidth: 0.5 },
            theme: "grid",
            didDrawPage: (data) => {
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();
                isLastPage = currentPage === totalPages;
                if (!isLastPage) {
                    doc.addImage(logo, "PNG", logoX, 20, logoWidth, logoHeight);
                    doc.setFont("Helvetica", "bold");
                    doc.setFontSize(20); 
                    doc.text(titleLines, titleX, titleY);
                }
            },
            didParseCell: (data) => {
                if (data.row.index === tableRows.length) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = [242, 242, 242];
                }
                if (data.row.index === tableRows.length + 1) {
                    data.cell.styles.fontStyle = "italic";
                    data.cell.styles.textColor = [100, 100, 100];
                    data.cell.colSpan = tableColumnHeaders.length;
                }
                if (data.row.index === tableRows.length + 2) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.textColor = [15, 67, 114];
                    data.cell.colSpan = tableColumnHeaders.length;
                }
            },
        });

        // --- Espacio extra para firmas debajo de la tabla ---
        let finalY = doc.lastAutoTable.finalY + 40; // Más espacio antes de las firmas
        const pageCenter = pageWidth / 2;
        const colWidth = (pageWidth - 80) / 2; // 40px de margen a cada lado

        // Línea de firma izquierda (usuario actual)
        doc.setFont("Helvetica", "normal");
        doc.setFontSize(11);
        doc.text("________________________________", 40, finalY);
        // Línea de firma derecha (firma secundaria si existe)
        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            doc.text("_____________________________________", 40 + colWidth, finalY);
        }
        // Nombres
        finalY += 13;
        doc.setFont("Helvetica", "normal");
        doc.setFontSize(10);
        doc.text(`Generado por: ${getUser().NombreSimple} ${getUser().ApellidoSimple}`, 40, finalY);
        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            doc.text(firmaSecundaria.nombre, 40 + colWidth, finalY);
        }
        // Cargos
        finalY += 11;
        const message = await handleMessageElaborated(getUser());
        doc.text(message, 40, finalY);
        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            doc.text(firmaSecundaria.cargo, 40 + colWidth, finalY);
        }

        doc.save(`${filename}.pdf`);

        await exportFileLogs(logTypePdf, IdSucursal, IdCaja, IdProvincia );
    };


    const exportToExcel = async () => {
        // Creamos un nuevo libro y hoja de Excel
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(nameReport.toUpperCase());
    
        // Formateamos los datos para exportar
        const formattedData = formatExportData(data, exportableColumns ?? columns);

        // Definimos las columnas de la hoja según los datos exportados
        worksheet.columns = Object.keys(formattedData[0]).map(key => ({
            key: key,
            width: 20,
        }));
    
        const numColumns = Object.keys(formattedData[0]).length;
    
        // Cargamos el logo y lo insertamos en la hoja
        const imagePath = '/img/logo-correos.png'; 
        const response = await fetch(imagePath);
        const imageBlob = await response.blob();
        const imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(imageBlob);
            reader.onloadend = () => {
                resolve(reader.result.split(',')[1]); 
            };
        });
    
        const imageId = workbook.addImage({
            base64: imageBase64,
            extension: 'png',
        });
    
        worksheet.addImage(imageId, {
            tl: { col: 0, row: 1 }, 
            ext: { width: 130, height: 50 }, 
            editAs: 'absolute', 
        });
    
        // Título principal del reporte
        const titleRange = `A3:${String.fromCharCode(64 + numColumns)}3`;
        worksheet.mergeCells(titleRange);

        const reportTextCell = worksheet.getCell('A3');
        reportTextCell.value = nameReport.toUpperCase();
        reportTextCell.font = { size: 20, bold: true };
        reportTextCell.alignment = { vertical: 'middle', horizontal: 'center' };
        reportTextCell.border = undefined;

        // Encabezado de columnas de la tabla
        worksheet.getRow(10).values = Object.keys(formattedData[0]);
    
        // Agregamos los datos de la tabla
        formattedData.forEach((row, index) => {
            worksheet.addRow({
                ...row,
                __rowNum__: index + 11 
            });
        });
    
        // Estilizamos el encabezado de la tabla
        worksheet.getRow(10).eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'ffd500' }, 
            };
            cell.font = {
                bold: true,
                color: { argb: '000000' },
            };
        });
    
        // Estilizamos todas las celdas de la tabla
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const isTitleCell = rowNumber === 3 && colNumber >= 1 && colNumber <= numColumns;
                if (cell.value && !isTitleCell) { 
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                } else {
                    cell.border = undefined; 
                }
            });
        });
    
        // Ajustamos el ancho de las columnas automáticamente
        worksheet.columns.forEach(column => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const columnLength = cell.value ? cell.value.toString().length : 10;
                if (columnLength > maxLength) {
                    maxLength = columnLength;
                }
            });
            column.width = maxLength < 10 ? 10 : maxLength;
        });

        // --- Ajuste de ancho mínimo ---
        const anchoMinimoNota = 200; 
        let anchoTotal = worksheet.columns.reduce((acc, col) => acc + col.width, 0);

        if (anchoTotal < anchoMinimoNota) {
            const extraPorColumna = (anchoMinimoNota - anchoTotal) / worksheet.columns.length;
            worksheet.columns.forEach(col => {
                col.width += extraPorColumna;
            });
        }
    
        // Agregamos autofiltro al encabezado de la tabla
        worksheet.autoFilter = {
            from: 'A10',
            to: `${String.fromCharCode(64 + numColumns)}10`,
        };
    
        // Coloreamos el fondo de las filas de información estática o personalizada
        const fillColor = '0F4372';
        const startRow = 6;
        const endRow = 9;
        const startCol = 1; 
        const endCol = numColumns; 
    
        for (let row = startRow; row <= endRow; row++) {
            for (let col = startCol; col <= endCol; col++) {
                const cell = worksheet.getCell(row, col);
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor },
                };
                cell.border = {};
            }
        }
    
        // Información estática por defecto para el encabezado (Sucursal, Fecha, Hora, Usuario)
        const staticInfo = {
            titulo: "Reporte de Ventas",
            sucursal: sucursalSelected ?? getUser().CodigoEstafeta + ' ' + getUser().NombreEstafeta,
            fecha: new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                .replace(/(\d+)\sde\s(\w+)\sde\s(\d+)/, '$2 $1, $3'), 
            hora: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }),
            usuario: getUser().Usuario,
        };
    
        // --- Encabezado personalizado o por defecto ---
        // Si customHeader es string o array de strings, se muestra como filas que ocupan todo el ancho
        // Si es array de objetos label/value, se muestra en dos columnas
        // Si no hay customHeader, se usa el encabezado por defecto
        if (customHeader) {
            // Si es string, lo convertimos a array de líneas
            let headerLines = [];
            if (typeof customHeader === 'string') {
                headerLines = customHeader.split(/\n|\r\n?/).filter(line => line.trim() !== '');
            } else if (Array.isArray(customHeader) && customHeader.every(item => typeof item === 'string')) {
                headerLines = customHeader;
            }
            if (headerLines.length > 0) {
                // Insertamos cada línea como una fila que abarca todas las columnas
                headerLines.forEach((line, idx) => {
                    const cellRange = `A${7 + idx}:${String.fromCharCode(64 + numColumns)}${7 + idx}`;
                    worksheet.mergeCells(cellRange);
                    worksheet.getCell(`A${7 + idx}`).value = line;
                    worksheet.getCell(`A${7 + idx}`).font = { bold: true, size: 13, name: 'Arial', color: { argb: 'FFFFFF' } };
                    worksheet.getCell(`A${7 + idx}`).alignment = { vertical: 'middle', horizontal: 'center' };
                });
            } else if (Array.isArray(customHeader) && customHeader.every(item => typeof item === 'object' && item.label && item.value)) {
                // Si es array de objetos label/value
                customHeader.forEach((item, idx) => {
                    worksheet.getCell(6 + idx, 1).value = item.label;
                    worksheet.getCell(6 + idx, 2).value = item.value;
                });
                // Aplicar estilos a las filas del encabezado personalizado
                for (let row = 6; row < 6 + customHeader.length; row++) {
                    for (let col = 1; col <= 2; col++) {
                        const cell = worksheet.getCell(row, col);
                        cell.font = {
                            bold: col === 1 ? true : false, 
                            color: { argb: 'FFFFFF' }, 
                            size: 11, 
                            name: 'Arial', 
                        };
                        cell.alignment = {
                            vertical: 'middle', 
                            horizontal: col === 1 ? 'right' : 'left', };
                    }
                }
            }
        } else {
            // Encabezado por defecto (Sucursal, Fecha, Hora, Usuario)
            worksheet.getCell(6, 1).value = "Estafeta:"; 
            worksheet.getCell(6, 2).value = staticInfo.sucursal; 
        
            worksheet.getCell(7, 1).value = "Fecha de Reporte:"; 
            worksheet.getCell(7, 2).value = staticInfo.fecha; 
        
            worksheet.getCell(8, 1).value = "Hora:"; 
            worksheet.getCell(8, 2).value = staticInfo.hora;
        
            worksheet.getCell(9, 1).value = "Usuario:"; 
            worksheet.getCell(9, 2).value = staticInfo.usuario; 
        
            for (let row = 6; row <= 9; row++) {
                for (let col = 1; col <= 2; col++) {
                    const cell = worksheet.getCell(row, col);
                    cell.font = {
                        bold: col === 1 ? true : false, 
                        color: { argb: 'FFFFFF' }, 
                        size: 11, 
                        name: 'Arial', 
                    };
                    cell.alignment = {
                        vertical: 'middle', 
                        horizontal: col === 1 ? 'right' : 'left', };
                }
            }
        }

        // --- Cálculo y agregado de totales ---
        if (includeTotals) {
            // Calculamos los totales usando los datos originales y las columnas exportables
            const totales = {};
            const columnsToUse = exportableColumns || columns;
            
            // Inicializamos los totales para las columnas totalizables
            columnsToUse.forEach(col => {
                if (col.isTotalizable) {
                    totales[col.accessor] = 0;
                }
            });

            // Sumamos los valores de cada columna totalizable
            data.forEach(row => {
                columnsToUse.forEach(col => {
                    if (col.isTotalizable && col.accessor) {
                        let valor = row[col.accessor];
                        
                        // Si el valor es una cadena, intentamos limpiarlo
                        if (typeof valor === "string") {
                            // Removemos símbolos de moneda y espacios
                            valor = valor.replace(/[B\/.\s,]/g, '');
                            valor = parseFloat(valor);
                        }
                        
                        // Verificamos que sea un número válido
                        if (!isNaN(valor) && valor !== null && valor !== undefined) {
                            totales[col.accessor] += Number(valor);
                        }
                    }
                });
            });

            // Agregamos la fila de totales al final
            const totalRowValues = [];
            columnsToUse.forEach((col, idx) => {
                if (col.isTotalizable && totales[col.accessor] !== undefined) {
                    totalRowValues.push(`B/. ${totales[col.accessor].toFixed(2)}`);
                }  else {
                    totalRowValues.push("");
                }
            });

            const totalRow = worksheet.addRow(totalRowValues);

            // Estilizamos la fila de totales
            totalRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true, size: 13 };
                cell.border = {
                    top: { style: 'thin', color: { argb: '000000' } },
                    left: { style: 'thin', color: { argb: '000000' } },
                    bottom: { style: 'thin', color: { argb: '000000' } },
                    right: { style: 'thin', color: { argb: '000000' } },
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });
        }

        // --- Notas y contacto ---
        // Agregamos la nota de responsabilidad y los datos de contacto al final del reporte
        const lastRow = worksheet.lastRow.number + 2;
        worksheet.mergeCells(`A${lastRow}:${String.fromCharCode(64 + numColumns)}${lastRow}`);
        worksheet.getCell(`A${lastRow}`).value = "Nota: Los colaboradores son responsables de todas las actividades realizadas con su cuenta de usuario en el sistema y deben asegurarse de que este documento sea utilizado exclusivamente por Correos Panamá.";
        worksheet.getCell(`A${lastRow}`).alignment = { vertical: 'middle', horizontal: 'center' };
    
        worksheet.mergeCells(`A${lastRow + 1}:${String.fromCharCode(64 + numColumns)}${lastRow + 1}`);
        worksheet.getCell(`A${lastRow + 1}`).value = "www.correospanama.gob.pa | Síguenos en: @correospma | Teléfonos: 512-7657 | WhatsApp: 6980-1551";
        worksheet.getCell(`A${lastRow + 1}`).alignment = { vertical: 'middle', horizontal: 'center' };
    
        const contactCells = worksheet.getCell(`A${lastRow + 1}`);
        contactCells.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '0F4372' }, 
        };
        contactCells.font = {
            color: { argb: 'FFFFFF' }, 
        };

        const noteCells = worksheet.getCell(`A${lastRow}`);
        noteCells.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F2F2F2' }, 
        };
        noteCells.font = {
            color: { argb: '000000'
            }, 
        };

        const espacioExtra = 4; 
        for (let i = 0; i < espacioExtra; i++) {
            worksheet.addRow([]);
        }
    
        // --- Información de quién generó el reporte y firmas  ---
        // Calcula la mitad de las columnas
        const mitad = Math.floor(numColumns / 2);

        // Fila para las líneas de firma
        const filaFirma = worksheet.lastRow.number + 2;
        worksheet.mergeCells(filaFirma, 1, filaFirma, mitad);
        worksheet.getCell(filaFirma, 1).value = "________________________________";
        worksheet.getCell(filaFirma, 1).alignment = { horizontal: 'center' };

        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            worksheet.mergeCells(filaFirma, mitad + 1, filaFirma, numColumns);
            worksheet.getCell(filaFirma, mitad + 1).value = "_____________________________________";
            worksheet.getCell(filaFirma, mitad + 1).alignment = { horizontal: 'center' };
        }

        // Fila para los nombres
        const filaNombres = filaFirma + 1;
        worksheet.mergeCells(filaNombres, 1, filaNombres, mitad);
        worksheet.getCell(filaNombres, 1).value = `Generado por: ${getUser().NombreSimple} ${getUser().ApellidoSimple}`;
        worksheet.getCell(filaNombres, 1).alignment = { horizontal: 'center' };

        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            worksheet.mergeCells(filaNombres, mitad + 1, filaNombres, numColumns);
            worksheet.getCell(filaNombres, mitad + 1).value = firmaSecundaria.nombre;
            worksheet.getCell(filaNombres, mitad + 1).alignment = { horizontal: 'center' };
        }

        // Fila para los cargos
        const filaCargos = filaNombres + 1;
        worksheet.mergeCells(filaCargos, 1, filaCargos, mitad);
        const message = await handleMessageElaborated(user);
        worksheet.getCell(filaCargos, 1).value = message;
        worksheet.getCell(filaCargos, 1).alignment = { horizontal: 'center' };

        if (firmaSecundaria && firmaSecundaria.nombre && firmaSecundaria.cargo) {
            worksheet.mergeCells(filaCargos, mitad + 1, filaCargos, numColumns);
            worksheet.getCell(filaCargos, mitad + 1).value = firmaSecundaria.cargo;
            worksheet.getCell(filaCargos, mitad + 1).alignment = { horizontal: 'center' };
        }

        // --- Guardado del archivo y registro de logs ---
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, `${filename}.xlsx`);

        await exportFileLogs(logTypeExcel, IdSucursal, IdCaja, IdProvincia);
    };

    return (
        <div>
            <div className="d-flex justify-content-between mb-3">
                <p>Se han encontrado <strong>{data.length}</strong> registros.</p>
                
                <div className="d-flex" style={{gap:10}}>
                
                {exportExcel && <button className="btn btn-success me-2" onClick={exportToExcel}>
                        <FaFileExcel style={{ color: "white" }} /> Exportar a Excel
                    </button>}

                {exportPdf &&
                <button className="btn btn-primary me-2" onClick={exportToPDF}>
                    <FaFilePdf style={{ color: "white" }} /> Exportar a PDF
                    </button>}
                </div>
            </div>
            <div style={{overflowX: 'auto', touchAction: 'pan-x', scrollBehavior: 'smooth'}} >
            <table {...getTableProps()} className="table table-striped table-bordered">
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => (
                                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
          

            <div className="d-flex justify-content-between align-items-center mt-3">
                <ReactPaginate
                    onPageChange={({ selected }) => gotoPage(selected)}
                    pageCount={Math.ceil(data.length / pageSize)}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    containerClassName="pagination"
                    activeClassName="active"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    previousLabel="Anterior"
                    nextLabel="Siguiente"
                />

                <div className="d-flex align-items-center">
                    <select
                        value={showCustomInput ? "custom" : pageSize}
                        onChange={handlePageSizeChange}
                        className="form-control w-auto"
                        style={{ marginRight: '5px', width: '120px' }}
                    >
                        {[5, 10, 20 ].map((size) => (
                            <option key={size} value={size}>
                                Mostrar {size}
                            </option>
                        ))}
                        <option value="custom">Personalizado</option>
                    </select>

                    {showCustomInput && (
                        <input
                            type="number"
                            value={customPageSize}
                            onChange={handleCustomPageSizeChange}
                            className="form-control w-auto"
                            style={{ width: '100px' }}
                            min="1"
                            placeholder="Ingresar"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataTable;